// eslint-disable-next-line no-restricted-imports -- legacy SCSS export, CSS Modules migration pending
import breakpoints from 'brand/app/scss/exports/breakpoints.scss';
import {isServer, parseCSSPropertyValue} from 'coreUtils/utils';

import {BaseBreakpoint, LargeImagesBreakpoint, TScrollBehavior, BreakpointCss, Breakpoint} from './IViewportMgr';

/**
 * @class ViewportMgr aimed to unify css breakpoints, @media mixin queries and js code.
 */
class ViewportMgr {
	private operationsMap = {
		'>': (a, b) => a > b,
		'>=': (a, b) => a >= b,
		'<': (a, b) => a < b,
		'<=': (a, b) => a <= b,
	};

	private breakpointsMap: Record<BaseBreakpoint, number> = {
		xs: 0, // We need this breakpoint to be 0 to make it work with the first breakpoint
		sm: +breakpoints['small'],
		md: +breakpoints['medium'],
		lg: +breakpoints['large'],
		xl: +breakpoints['x-large'],
		xxl: +breakpoints['xx-large'],
	};

	private largeImagesBreakpointsMap: Record<LargeImagesBreakpoint, number> = {
		xxxl: +breakpoints['xxx-large'],
		xxxxl: +breakpoints['xxxx-large'],
		...(breakpoints['xxxxx-large'] !== undefined && {
			'xxxxxl': +breakpoints['xxxxx-large'],
		}),
	};

	private allBreakpointsMap: Record<Breakpoint, number> = {
		...this.breakpointsMap,
		...this.largeImagesBreakpointsMap,
	};

	private breakpointsCssMap: Record<BreakpointCss, Breakpoint> = {
		'extra-small': 'xs',
		'small': 'sm',
		'medium': 'md',
		'large': 'lg',
		'x-large': 'xl',
		'xx-large': 'xxl',
		'xxx-large': 'xxxl',
		'xxxx-large': 'xxxxl',
		'xxxxx-large': 'xxxxxl',
	};

	private cache = new Map<string, any>();

	/**
	 * Represents ViewportMgr
	 */
	constructor() {
		Object.freeze(this.breakpointsMap);
		Object.freeze(this.largeImagesBreakpointsMap);
		Object.freeze(this.allBreakpointsMap);
		Object.freeze(this.breakpointsCssMap);
		Object.freeze(this.operationsMap);
	}

	/**
	 * Parses a query string to extract the operator and operand values.
	 * @param {string} query - The query string to be parsed.
	 * @returns {[string, string|number]} - An array containing the operator and operand values respectively.
	 */
	parseQuery(query: string): [string, string|number] {
		const [, operator, rawOperand] = query.match(/^(<|>|<=|>=)\s*([-\w]+)/);

		const operand = /^\d+$/.test(rawOperand)
			? Number(rawOperand)
			: rawOperand;

		return [operator, operand];
	}

	/**
	 * Takes window width and one or more query expressions of the same format @media scss mixin use
	 * and calculate whether width match queries
	 * @param width {Number} window width
	 * @param queries {Array} array of queries
	 * @returns {Boolean}
	 */
	media(width: number, ...queries: string[]): boolean {
		return queries.every((query) => this.query(query, width));
	}

	/**
	 * Returns true if viewport is in the specified breakpoint (as "useMedia()")
	 * @param query {string} breakpoint name preceded by an operator
	 * @param width {Number} window width
	 * @returns {Boolean}
	 */
	query(query: string, width?: number): boolean {
		const [operator, operand] = this.parseQuery(query);
		const operandValue = typeof operand === 'number'
			? operand
			: breakpoints[operand];

		return this.operationsMap[operator](width || window.innerWidth, operandValue);
	}

	/**
	 * Returns true if viewport is greater than the specified breakpoint
	 * @param breakpoint {BreakpointCss} breakpoint name
	 * @param equals {Boolean} include equal sign in the rule if true
	 * @returns {Boolean}
	 */
	isGreaterThan(breakpoint: BreakpointCss, equals: boolean = true): boolean {
		const query = equals ? `>=${breakpoint}` : `>${breakpoint}`;

		return this.media(window.innerWidth, query);
	}

	/**
	 * Returns true if viewport is less than the specified breakpoint
	 * @param breakpoint {BreakpointCss} breakpoint name
	 * @param equals {Boolean} include equal sign in the rule if true
	 * @returns {Boolean}
	 */
	isLessThan(breakpoint: BreakpointCss, equals: boolean = true): boolean {
		const query = equals ? `<=${breakpoint}` : `<${breakpoint}`;

		return this.media(window.innerWidth, query);
	}

	/**
	 * Returns all the breakpoints minimized names
	 * @returns {Array}
	 */
	getBreakpoints(includeLargeImagesBreakpoints = false): Array<any> {
		const breakpointsMap = this.getBreakpointsMap(includeLargeImagesBreakpoints);
		const cacheKey = includeLargeImagesBreakpoints ? 'allBreakpoints' : 'breakpoints';

		if (!this.cache.has(cacheKey)) {
			this.cache.set(cacheKey, Object.keys(breakpointsMap));
		}

		return this.cache.get(cacheKey) as Array<keyof typeof breakpointsMap>;
	}

	/**
	 * Returns all the breakpoints values
	 * @returns {Array<number>}
	 */
	getBreakpointsValues(includeLargeImagesBreakpoints = false): Array<string> {
		const breakpointsMap = this.getBreakpointsMap(includeLargeImagesBreakpoints);
		const cacheKey = includeLargeImagesBreakpoints ? 'allBreakpointsValues' : 'breakpointsValues';

		if (!this.cache.has(cacheKey)) {
			this.cache.set(cacheKey, Object.values(breakpointsMap));
		}

		return this.cache.get(cacheKey) as Array<keyof typeof breakpointsMap>;
	}

	/**
	 * Returns all the breakpoint extended map
	 * @returns {Record<BreakpointCss, Breakpoint>}
	 */
	getBreakpointsExtendedMap(): Record<BreakpointCss, Breakpoint> {
		return this.breakpointsCssMap;
	}

	/**
	 * Returns largeImages breakpoints as minimized names
	 * @returns {Array}
	 */
	getLargeImagesBreakpoints(): Array<LargeImagesBreakpoint> {
		const cacheKey = 'largeImagesBreakpoints';

		if (!this.cache.has(cacheKey)) {
			const largeImagesBreakpointsMap = this.largeImagesBreakpointsMap;

			this.cache.set(cacheKey, Object.keys(largeImagesBreakpointsMap));
		}

		return this.cache.get(cacheKey) as Array<
			keyof typeof this.largeImagesBreakpointsMap
		>;
	}

	/**
	 * Returns all the breakpoints values
	 * @returns {Record<BaseBreakpoint, number>}
	 */
	getBreakpointsMap(includeLargeImagesBreakpoints = false): Record<BaseBreakpoint, number> {
		if (includeLargeImagesBreakpoints) {
			const cacheKey = 'allBreakpointsMap';

			if (!this.cache.has(cacheKey)) {
				this.cache.set(cacheKey, Object.freeze({...this.breakpointsMap, ...this.largeImagesBreakpointsMap}));
			}

			return this.cache.get(cacheKey);
		} else {
			return this.breakpointsMap;
		}
	}

	/**
	 * Returns all the large images breakpoints values
	 * @returns {Record<LargeImagesBreakpoint, number>}
	 */
	getLargeImagesBreakpointsMap(): Record<LargeImagesBreakpoint, number> {
		return this.largeImagesBreakpointsMap;
	}

	/**
	 * Returns true if viewport is mobile or false if viewport is desktop
	 * @return {Boolean}
	 */
	isMobile(): boolean {
		return this.getCurrentBreakpoint() === 'xs';
	}

	/**
	 * Returns true if viewport is (mobile or tablet) or false if viewport is desktop
	 * @return {Boolean}
	 */
	isMobileOrTablet() : boolean {
		return this.media(window.innerWidth, '<=x-large');
	}

	/**
	 * Returns the current viewport breakpoint's minimize name
	 * @returns {String}
	 */
	getCurrentBreakpoint(includeLargeImagesBreakpoints: boolean = false): string {
		if (isServer) {
			return 'xs';
		}

		const breakpoints = this.getBreakpoints(includeLargeImagesBreakpoints);
		const breakpointsLen = breakpoints.length;

		for (let i = 0; i < breakpointsLen; i += 1) {
			const breakpoint = breakpoints[i];
			const isSmaller = window.innerWidth < this.allBreakpointsMap[breakpoint];

			// XS breakpoint
			if (isSmaller && i === 0) {
				return 'xs';
			}

			// Rest
			if (isSmaller) {
				return breakpoints[i - 1];
			}
		}

		// XXL as default
		return breakpoints[breakpointsLen - 1];
	}

	/**
	 * Scrolls the viewport to the specified position
	 * @param {Number} y {Number} Y position
	 * @param {Number} x {Number} X position
	 * @param {String} behavior {String} Behaviour
	 */
	scrollTo(y: number, x: number = 0, behavior: TScrollBehavior = 'instant'): void {
		if (typeof window !== 'undefined') {
			window.scrollTo({
				top: y,
				left: x,
				behavior: behavior,
			});
		}
	}

	/**
	 * Returns screen orientation
	 * @returns {string}
	 */
	getScreenOrientation(): string {
		if (isServer) {
			return 'portrait';
		}

		return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
	}

	/**
	 * @function
	 * @description Returns the header height in px
	 * @returns {Number} returns total height or 0 in case header is hidden
	 */
	getHeaderHeightTotal = (): number => {
		if (isServer) {
			return 0;
		}

		const windowComputedStyle = window.getComputedStyle(document.getElementById('app-main'));
		const heightString = windowComputedStyle.getPropertyValue('--header-height') || windowComputedStyle.getPropertyValue('--header-total-height');

		return parseCSSPropertyValue(heightString);
	};

	/**
	 * Returns scroll position
	 * @returns {scrollX: number, scrollY: number}
	 */
	getScrollPosition(): { scrollX: number; scrollY: number } {
		if (isServer) {
			return {
				scrollX: 0,
				scrollY: 0,
			};
		}

		return {
			scrollX: window.scrollX,
			scrollY: window.scrollY,
		};
	}
}

export default new ViewportMgr();
